import { useEffect, useId, useState } from 'react';
import {
  IconBroadcast,
  IconBuildingStore,
  IconCheck,
  IconChevronDown,
  IconWorldWww,
} from '@tabler/icons-react';
import { WP_ICON_NAV, WP_ICON_STROKE } from '../icons/constants.js';
import menuStyles from './data-table/DataTableMenus.module.css';
import { Button } from '../primitives/Button.js';
import { IconButton } from '../primitives/IconButton.js';
import { Paper } from '../primitives/Paper.js';
import { Popover } from '../primitives/Popover.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../utils/cx.js';
import popoverStyles from './PublishChannelPopover.module.css';

const PILL_ICON = { size: 14, stroke: WP_ICON_STROKE } as const;

const DEFAULT_SHOWROOM_LOCKED_HINT =
  'Stock point de vente nul — réceptionnez ou transférez vers un emplacement showroom d’abord.';

export interface PublishChannelPopoverProps {
  storefront: boolean;
  showroom: boolean;
  onChange: (next: { storefront: boolean; showroom: boolean }) => void;
  disabled?: boolean;
  disabledHint?: string;
  /** Lock turning showroom ON (outlet ATP = 0). Turning OFF always allowed. */
  showroomLocked?: boolean;
  showroomLockedHint?: string;
  /** `menuRow` = full-width list row (icon + « Canaux » + chevron), for nested overflow menus. Default = compact toolbar icon. */
  menuTrigger?: 'icon' | 'menuRow';
}

export function PublishChannelPopover({
  storefront,
  showroom,
  onChange,
  disabled,
  disabledHint,
  showroomLocked = false,
  showroomLockedHint = DEFAULT_SHOWROOM_LOCKED_HINT,
  menuTrigger = 'icon',
}: PublishChannelPopoverProps) {
  const [opened, setOpened] = useState(false);
  const [sf, setSf] = useState(storefront);
  const [sh, setSh] = useState(showroom);
  const panelId = useId();
  const titleId = useId();

  useEffect(() => {
    setSf(storefront);
    setSh(showroom);
  }, [storefront, showroom]);

  const dirty = sf !== storefront || sh !== showroom;
  const showroomToggleDisabled = Boolean(disabled) || (showroomLocked && !sh);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      portal
      align="end"
      panelId={panelId}
      ariaLabelledBy={titleId}
      portalMinWidth={248}
      portalMaxWidth={280}
      portalEstimatedHeight={showroomLocked ? 240 : 200}
      target={
        menuTrigger === 'menuRow' ? (
          <button
            type="button"
            className={menuStyles.rowMenuItem}
            disabled={disabled && !disabledHint}
            aria-label="Canaux de vente (site web et showroom)"
            aria-haspopup="dialog"
          >
            <span className={popoverStyles.menuRowInner}>
              <IconBroadcast size={14} stroke={WP_ICON_STROKE} aria-hidden style={{ flexShrink: 0 }} />
              <Text component="span" size="sm" ff="body" style={{ textAlign: 'left' }}>
                Canaux
              </Text>
              <IconChevronDown
                size={14}
                stroke={WP_ICON_STROKE}
                aria-hidden
                className={popoverStyles.menuRowChevron}
              />
            </span>
          </button>
        ) : (
          <IconButton
            type="button"
            variant="ghost"
            size="xs"
            borderless
            disabled={disabled && !disabledHint}
            aria-label="Canaux de vente"
            aria-haspopup="dialog"
            className={popoverStyles.iconTrigger}
          >
            <IconBroadcast {...WP_ICON_NAV} aria-hidden />
          </IconButton>
        )
      }
    >
      <div id={panelId} className={popoverStyles.panel}>
        <div id={titleId} className={popoverStyles.panelTitle}>
          Diffusion par canal
        </div>

        {disabled && disabledHint ? (
          <Paper p="xs" radius="sm" className={popoverStyles.hintPaper}>
            <Text size="xs" ta="center" component="div" ff="body">
              {disabledHint}
            </Text>
          </Paper>
        ) : null}

        <div className={popoverStyles.channelPillGrid} role="group" aria-label="Canaux de vente">
          <button
            type="button"
            className={cx(
              popoverStyles.channelPill,
              popoverStyles.channelPillWeb,
              sf && popoverStyles.channelPillActive,
              disabled && popoverStyles.channelPillDisabled,
            )}
            disabled={disabled}
            aria-pressed={sf}
            aria-label="Site web (boutique en ligne)"
            onClick={() => setSf((v) => !v)}
          >
            {sf ? (
              <IconCheck {...PILL_ICON} aria-hidden className={popoverStyles.channelPillMark} />
            ) : (
              <IconWorldWww {...PILL_ICON} aria-hidden className={popoverStyles.channelPillMark} />
            )}
            <span className={popoverStyles.channelPillLabel}>Web</span>
          </button>
          <button
            type="button"
            className={cx(
              popoverStyles.channelPill,
              popoverStyles.channelPillShowroom,
              sh && popoverStyles.channelPillActive,
              showroomToggleDisabled && popoverStyles.channelPillDisabled,
            )}
            disabled={showroomToggleDisabled}
            title={showroomToggleDisabled && showroomLocked ? showroomLockedHint : undefined}
            aria-pressed={sh}
            aria-label={
              showroomToggleDisabled && showroomLocked
                ? `Point de vente — ${showroomLockedHint}`
                : 'Point de vente'
            }
            onClick={() => {
              if (showroomToggleDisabled) return;
              setSh((v) => !v);
            }}
          >
            {sh ? (
              <IconCheck {...PILL_ICON} aria-hidden className={popoverStyles.channelPillMark} />
            ) : (
              <IconBuildingStore
                {...PILL_ICON}
                aria-hidden
                className={popoverStyles.channelPillMark}
              />
            )}
            <span className={popoverStyles.channelPillLabel}>Showroom</span>
          </button>
        </div>

        {!disabled && showroomLocked && !sh ? (
          <Text size="xs" ta="center" component="div" ff="body" className={popoverStyles.showroomLockHint}>
            {showroomLockedHint}
          </Text>
        ) : null}

        {!disabled ? (
          <div className={popoverStyles.footer}>
            <Button
              size="sm"
              type="button"
              variant="primary"
              className={popoverStyles.footerButton}
              disabled={!dirty}
              onClick={() => {
                const nextShowroom = showroomLocked && !showroom ? false : sh;
                onChange({ storefront: sf, showroom: nextShowroom });
                setOpened(false);
              }}
            >
              Mettre à jour
            </Button>
          </div>
        ) : null}
      </div>
    </Popover>
  );
}
